import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Spin } from 'antd';
import classNames from 'classnames';
import { Icon, Input } from 'Components';
import { useStores } from 'Hooks/useStore';
import { Status } from 'app-types';
import {
	ASSIGN_USERS_OPTION,
	options
} from 'LearningManager/constants/createAssignment.constant';
import CheckBoxCard from 'LearningManager/components/CheckBoxCard';
import MultiSelectDropdown from 'LearningManager/components/MultiSelectDropdown';
import useAudienceTenantId from 'LearningManager/hooks/useAudienceTenantId';
import usePagedProperties from 'LearningManager/hooks/usePagedProperties';
import InfinityScrollWrapper from 'Components/InfinityScroll/InfinityScrollWrapper';
import AssignmentUsersPicker from './AssignmentUsersPicker';
import styles from './AssignmentActionFlowModal.module.scss';

const ENTITY_LIST_HEIGHT = 360;

export interface AudienceSelection {
	methodId: number | null;
	entityIds: number[];
	userIds: string[];
	groupPropertyIds: number[];
	allGroupProperties: boolean;
}

export const EMPTY_AUDIENCE: AudienceSelection = {
	methodId: null,
	entityIds: [],
	userIds: [],
	groupPropertyIds: [],
	allGroupProperties: false
};

export const hasAudienceSelection = (value: AudienceSelection): boolean =>
	value.methodId === ASSIGN_USERS_OPTION.INDIVIDUAL
		? value.userIds.length > 0
		: value.entityIds.length > 0;

const ENTITY_NOUN: Record<number, string> = {
	[ASSIGN_USERS_OPTION.ORGANIZATION]: 'organizations',
	[ASSIGN_USERS_OPTION.PROPERTY]: 'properties',
	[ASSIGN_USERS_OPTION.ROLE]: 'roles',
	[ASSIGN_USERS_OPTION.GROUP]: 'groups'
};

interface AudienceEntity {
	id: number;
	name: string;
	label: string;
}

interface Props {
	assignmentId: number;
	scopes: string[];
	tenantId?: number;
	value: AudienceSelection;
	onChange: (value: AudienceSelection) => void;
}

const AssignmentAudiencePicker: React.FC<Props> = observer(
	({ assignmentId, scopes, tenantId, value, onChange }) => {
		const { assignUsersStore } = useStores();
		const { assignmentOrganizations, groups, roles, status } = assignUsersStore;

		const [search, setSearch] = useState('');

		const { methodId, groupPropertyIds, allGroupProperties } = value;

		const setGroupPropertyIds = (ids: number[], allSelected: boolean) =>
			onChange({
				...value,
				groupPropertyIds: allSelected ? [] : ids,
				allGroupProperties: allSelected
			});

		const { tenantId: audienceTenantId, isTenantResolved } =
			useAudienceTenantId(tenantId);

		const isPropertyMethod = methodId === ASSIGN_USERS_OPTION.PROPERTY;
		const {
			pagedProperties,
			setSearch: setPropertySearch,
			isSearchPending: isPropertySearchPending,
			dropdownProps: propertyDropdownProps
		} = usePagedProperties(
			audienceTenantId,
			isTenantResolved &&
				(isPropertyMethod || methodId === ASSIGN_USERS_OPTION.GROUP)
		);
		const hasMoreProperties =
			isPropertyMethod && propertyDropdownProps.hasMore;

		useEffect(() => {
			if (!isTenantResolved) return;
			if (methodId === ASSIGN_USERS_OPTION.ORGANIZATION) {
				assignUsersStore
					.fetchAssignmentOrganizations(audienceTenantId)
					.catch(() => undefined);
			}
		}, [methodId, isTenantResolved, audienceTenantId]);

		useEffect(() => {
			if (isPropertyMethod) setPropertySearch(search);
		}, [search, isPropertyMethod]);

		useEffect(() => {
			if (methodId !== ASSIGN_USERS_OPTION.ROLE || roles.length) return;
			assignUsersStore.fetchRoles().catch(() => undefined);
		}, [methodId]);

		useEffect(() => {
			if (methodId !== ASSIGN_USERS_OPTION.GROUP) return;
			if (allGroupProperties && !isTenantResolved) return;
			assignUsersStore
				.fetchGroupsByProperty(groupPropertyIds, {
					allProperties: allGroupProperties,
					tenantId: audienceTenantId
				})
				.catch(() => undefined);
		}, [
			methodId,
			groupPropertyIds,
			allGroupProperties,
			isTenantResolved,
			audienceTenantId
		]);

		const selectMethod = (nextMethodId: number) => {
			setSearch('');
			onChange({ ...EMPTY_AUDIENCE, methodId: nextMethodId });
		};

		const toggleEntity = (id: number | string) => {
			const numId = Number(id);
			const entityIds = value.entityIds.includes(numId)
				? value.entityIds.filter((entityId) => entityId !== numId)
				: [...value.entityIds, numId];
			onChange({ ...value, entityIds });
		};

		const toggleUser = (userId: string) => {
			const userIds = value.userIds.includes(userId)
				? value.userIds.filter((id) => id !== userId)
				: [...value.userIds, userId];
			onChange({ ...value, userIds });
		};

		const entities: AudienceEntity[] =
			methodId === ASSIGN_USERS_OPTION.ORGANIZATION
				? assignmentOrganizations
				: isPropertyMethod
				? pagedProperties.items
				: methodId === ASSIGN_USERS_OPTION.ROLE
				? roles.map((role) => ({ ...role, label: 'Role' }))
				: methodId === ASSIGN_USERS_OPTION.GROUP
				? groups
				: [];

		const filtered =
			search.trim() && !isPropertyMethod
			? entities.filter((entity) =>
					entity.name.toLowerCase().includes(search.toLowerCase())
			  )
			: entities;

		const isAllSelected =
			!hasMoreProperties &&
			filtered.length > 0 &&
			filtered.every((entity) => value.entityIds.includes(entity.id));

		const handleSelectAll = async () => {
			if (isAllSelected) {
				const listedIds = new Set(filtered.map((entity) => entity.id));
				onChange({
					...value,
					entityIds: value.entityIds.filter((id) => !listedIds.has(id))
				});
				return;
			}
			let pool: AudienceEntity[] = filtered;
			if (hasMoreProperties) {
				const allProperties = await pagedProperties
					.loadAll()
					.catch(() => undefined);
				if (!allProperties) return;
				pool = allProperties;
			}
			onChange({
				...value,
				entityIds: [
					...new Set([
						...value.entityIds,
						...pool.map((entity) => entity.id)
					])
				]
			});
		};

		const isLoadingEntities =
			status.fetchAssignmentOrganizations === Status.Pending ||
			(isPropertyMethod &&
				(pagedProperties.status === Status.Pending ||
					isPropertySearchPending)) ||
			status.fetchRoles === Status.Pending ||
			status.fetchGroups === Status.Pending;

		const renderEntityList = () => (
			<>
				<div className={styles.pickerFilters}>
					{methodId === ASSIGN_USERS_OPTION.GROUP && (
						<MultiSelectDropdown
							{...propertyDropdownProps}
							placeholder="Filter by property"
							defaultSelectedIds={groupPropertyIds}
							defaultAllSelected={allGroupProperties}
							onChange={setGroupPropertyIds}
						/>
					)}
					<Input.Search
						id="lms-ca-action-entity-search"
						className={styles.pickerSearch}
						placeholder="Search"
						suffix={<Icon type="search" role="img" />}
						allowClear
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</div>

				<div className={styles.usersHeader}>
					Select {ENTITY_NOUN[methodId as number] ?? 'items'}
					<span className={styles.required} aria-hidden="true">
						*
					</span>
					{filtered.length > 1 && (
						<span
							id="lms-ca-action-select-all"
							className={styles.selectAll}
							onClick={handleSelectAll}
						>
							{isAllSelected ? 'Deselect all' : 'Select all'}
						</span>
					)}
				</div>

				{isLoadingEntities ? (
					<div className={styles.pickerLoading}>
						<Spin />
					</div>
				) : filtered.length === 0 ? (
					<div className={styles.pickerEmpty}>
						{methodId === ASSIGN_USERS_OPTION.GROUP &&
						!groupPropertyIds.length &&
						!allGroupProperties
							? 'Pick a property to see its groups.'
							: 'Nothing to show here.'}
					</div>
				) : (
					<InfinityScrollWrapper
						dataLength={filtered.length}
						hasMore={hasMoreProperties}
						onLoadMore={propertyDropdownProps.onLoadMore}
						loading={pagedProperties.loadMoreStatus === Status.Pending}
						scrollableId="lms-ca-action-entity-scroll"
						endMessageText=""
						height={ENTITY_LIST_HEIGHT}
					>
					<div
						className={styles.specificSubContent}
						style={{ maxHeight: 'none', overflowY: 'visible' }}
					>
						{filtered.map((entity) => (
							<CheckBoxCard
								key={entity.id}
								id={entity.id}
								htmlId={`lms-ca-action-entity-${entity.id}`}
								icon={<Icon type="propOptIcon" />}
								title={entity.name}
								desc={entity.label}
								isChecked={value.entityIds.includes(entity.id)}
								onChange={toggleEntity}
							/>
						))}
					</div>
					</InfinityScrollWrapper>
				)}
			</>
		);

		return (
			<div id="lms-ca-assignment-audience-picker">
				<div className={styles.specificMethods}>
					{options.map((option) => {
						const isSelected = methodId === option.id;
						return (
							<div
								key={option.id}
								id={`lms-ca-action-method-${option.id}`}
								className={classNames(styles.methodCard, {
									[styles['methodCard--selected']]: isSelected
								})}
								onClick={() => selectMethod(option.id)}
							>
								<div className={styles.methodLabel}>
									{isSelected ? option.selectedIcon : option.icon}
									<span className={styles.methodHead}>
										{option.title}
									</span>
								</div>
								<div className={styles.methodValue}>
									{option.desc}
								</div>
							</div>
						);
					})}
				</div>

				{methodId === ASSIGN_USERS_OPTION.INDIVIDUAL ? (
					<AssignmentUsersPicker
						assignmentId={assignmentId}
						scopes={scopes}
						tenantId={tenantId}
						selectedUserIds={value.userIds}
						onToggle={toggleUser}
					/>
				) : methodId ? (
					renderEntityList()
				) : (
					<div className={styles.pickerEmpty}>
						Choose a selection method to continue.
					</div>
				)}
			</div>
		);
	}
);

export default AssignmentAudiencePicker;
